import React, { useState, useEffect, useRef } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

export default function Header() {
  const [isOpen, setIsOpen] = useState(false)
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)
  const mobileDropdownRef = useRef(null)
  const location = useLocation()

  // Close menus on route change
  useEffect(() => {
    setIsOpen(false)
    setIsDropdownOpen(false)
  }, [location])

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const handleContactClick = (e) => {
    e.preventDefault()
    setIsDropdownOpen(false)
    setIsOpen(false)
    const contactSection = document.getElementById('kontakt-sektion')
    if (contactSection) {
      contactSection.scrollIntoView({ behavior: 'smooth' })
    } else {
      // If not on home page, navigate to home and scroll
      window.location.href = '/#kontakt-sektion'
    }
  }

  return (
    <header className="w-full bg-white border-b border-slate-200 sticky top-0 z-50 shadow-sm">
      {/* Top Bar */}
      <div className="bg-brand-blue text-white py-2 px-4 text-xs sm:text-sm">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row justify-between items-center gap-2">
          <div className="flex flex-wrap justify-center gap-4 sm:gap-6">
            <a href="tel:+4522141149" className="flex items-center gap-2 hover:text-brand-orange transition-colors whitespace-nowrap">
              <i className="fa-solid fa-phone text-brand-orange" aria-hidden="true"></i>
              <span>+45 22 14 11 49</span>
            </a>
            <a href="mailto:dn@trapezdk.dk?subject=Henvendelse%20via%20hjemmesiden" className="flex items-center gap-2 hover:text-brand-orange transition-colors whitespace-nowrap">
              <i className="fa-solid fa-envelope text-brand-orange" aria-hidden="true"></i>
              <span>dn@trapezdk.dk</span>
            </a>
          </div>
          <a 
            href="https://g.co/kgs/kkWo3Wq" 
            target="_blank" 
            rel="noopener noreferrer" 
            className="flex items-center gap-2 hover:text-brand-orange transition-colors text-center sm:text-right whitespace-nowrap"
          >
            <i className="fa-solid fa-location-dot text-brand-orange" aria-hidden="true"></i>
            <span>Rosbjergvej 54C, 8220 Brabrand</span>
          </a>
        </div>
      </div>

      {/* Main Header */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex justify-between items-center">
        {/* Logo */}
        <Link to="/" className="flex items-center">
          <img 
            src="https://assets.auxodev.dk/cdn-cgi/image/width=360,quality=90,format=auto/assets/28021fd9-fcdc-43f4-860a-5599c796fdc7/677bab25e480f9dca9762cec-trapezdklogo.png" 
            alt="Trapezdk Logo" 
            className="h-10 sm:h-12 w-auto object-contain"
          />
        </Link>

        {/* Desktop Navigation (Visible on screens >= 1200px) */}
        <nav className="hidden min-[1200px]:flex items-center gap-8 font-medium">
          <NavLink 
            to="/" 
            className={({ isActive }) => 
              `hover:text-brand-orange transition-colors py-2 border-b-2 ${isActive ? 'border-brand-orange text-brand-orange' : 'border-transparent text-slate-700'}`
            }
            end
          >
            Forside
          </NavLink>
          <NavLink 
            to="/om-os" 
            className={({ isActive }) => 
              `hover:text-brand-orange transition-colors py-2 border-b-2 ${isActive ? 'border-brand-orange text-brand-orange' : 'border-transparent text-slate-700'}`
            }
          >
            Om os
          </NavLink>
          <NavLink 
            to="/referencer" 
            className={({ isActive }) => 
              `hover:text-brand-orange transition-colors py-2 border-b-2 ${isActive ? 'border-brand-orange text-brand-orange' : 'border-transparent text-slate-700'}`
            }
          >
            Referencer
          </NavLink>
          <NavLink 
            to="/tilbud" 
            className={({ isActive }) => 
              `hover:text-brand-orange transition-colors py-2 border-b-2 ${isActive ? 'border-brand-orange text-brand-orange' : 'border-transparent text-slate-700'}`
            }
          >
            Indhent tilbud
          </NavLink>
        </nav>

        {/* Burger Button (Visible on screens < 1200px) */}
        <button 
          onClick={() => setIsOpen(!isOpen)}
          className="flex min-[1200px]:hidden items-center justify-center p-2 rounded-lg text-slate-700 hover:bg-slate-100 focus:outline-none transition-colors"
          aria-label="Menu"
        >
          <i className={`fa-solid ${isOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`}></i>
        </button>
      </div>

      {/* Mobile Navigation Menu (Visible on screens < 1200px) */}
      {isOpen && (
        <div className="min-[1200px]:hidden border-t border-slate-200 bg-white py-4 px-4 shadow-inner animate-in slide-in-from-top duration-200">
          <div className="flex flex-col gap-4">
            <NavLink 
              to="/" 
              className={({ isActive }) => 
                `block px-3 py-2 rounded-lg font-medium transition-colors ${isActive ? 'bg-slate-100 text-brand-orange' : 'text-slate-700 hover:bg-slate-50'}`
              }
              end
            >
              Forside
            </NavLink>
            <NavLink 
              to="/om-os" 
              className={({ isActive }) => 
                `block px-3 py-2 rounded-lg font-medium transition-colors ${isActive ? 'bg-slate-100 text-brand-orange' : 'text-slate-700 hover:bg-slate-50'}`
              }
            >
              Om os
            </NavLink>
            <NavLink 
              to="/referencer" 
              className={({ isActive }) => 
                `block px-3 py-2 rounded-lg font-medium transition-colors ${isActive ? 'bg-slate-100 text-brand-orange' : 'text-slate-700 hover:bg-slate-50'}`
              }
            >
              Referencer
            </NavLink>
            <NavLink 
              to="/tilbud" 
              className={({ isActive }) => 
                `block px-3 py-2 rounded-lg font-medium transition-colors ${isActive ? 'bg-slate-100 text-brand-orange' : 'text-slate-700 hover:bg-slate-50'}`
              }
            >
              Indhent tilbud
            </NavLink>
          </div>
        </div>
      )}
    </header>
  )
}
